"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, HardHat, ShieldCheck, Landmark, ArrowRight, CircleCheck as CheckCircle2, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const commercialCapabilities = [{
  id: "main-contractors",
  title: "Main Contractors",
  icon: HardHat,
  description: "Multi-level independent scaffolding, birdcage systems, and loading bays engineered for structural work and timed trades.",
  specs: "NASC compliant handovers, weekly inspections, 100% PPE adherence"
}, {
  id: "developers",
  title: "Developers & Housebuilders",
  icon: Building2,
  description: "Phased plot access, progressive lifts, and temporary roof coverage to keep new-build and regeneration schemes running on programme.",
  specs: "Dedicated supervisor contact, planned phased builds, tidy strike-down"
}, {
  id: "local-councils",
  title: "Councils & Public Sector",
  icon: Landmark,
  description: "Pedestrian gantry protection, high-containment debris netting, and public-facing safety measures for civic and education projects.",
  specs: "NASC & CHAS certified, clear RAMS, public safety controls"
}, {
  id: "facilities-management",
  title: "Facilities Management",
  icon: ShieldCheck,
  description: "Rapid-response maintenance towers, plant access platforms, and flexible scheduling to minimise disruption for active commercial buildings.",
  specs: "Minimal tenant disruption, flexible shifts, clean operational footprints"
}];
const accreditationBadges = [{
  name: "NASC",
  label: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS",
  label: "Contractors Health & Safety Scheme"
}];
function OverviewContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const fallbackSrc = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_overview_site_gantry.webp";
  const fallbackAlt = "Multi-storey commercial development scaffolding with pedestrian gantry and containment netting";
  const targetPhoto = posts?.find((p: any) => {
    const photoName = p.customFields?.photo_name || p.acf?.photo_name || p.title;
    return photoName === "Commercial Overview Image";
  });
  const photoImage = targetPhoto?.customFields?.image || (targetPhoto as any)?.acf?.image;
  const photoAlt = targetPhoto?.customFields?.alt_text || (targetPhoto as any)?.acf?.alt_text;
  const imgSrc = typeof photoImage === "string" && photoImage.trim() !== "" ? photoImage : fallbackSrc;
  const imgAlt = typeof photoAlt === "string" && photoAlt.trim() !== "" ? photoAlt : fallbackAlt;
  const isFallback = imgSrc === fallbackSrc;
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="commercial-overview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {/* Top Answer-First Summary & Photographic Anchor */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-16 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            {/* Structural Datum Rule */}
            <div className="w-12 h-1 bg-primary mb-4 rounded-full" />
            <span className="text-xs md:text-sm uppercase tracking-widest font-bold text-muted-foreground mb-2">
              Commercial Capability Overview
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default mb-5 leading-tight">
              Scaffolding built for commercial schedules and safety audits.
            </h1>
            <p className="text-lg text-muted-foreground font-sans leading-relaxed mb-6">
              BMF Scaffolding delivers engineered access, pedestrian protection, and temporary roofing for main contractors, developers, local authorities, and facilities managers across the South East. Led by Pete Wood&apos;s 20+ years of industry experience, we ensure tight site coordination, reliable strike dates, and strict safety compliance on every level.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full mb-8">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground bg-muted/60 px-3.5 py-2.5 rounded-md border border-border">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>NASC & CHAS verified</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground bg-muted/60 px-3.5 py-2.5 rounded-md border border-border">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>South East, Home Counties & London coverage</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground bg-muted/60 px-3.5 py-2.5 rounded-md border border-border">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Full RAMS & weekly inspection logs</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground bg-muted/60 px-3.5 py-2.5 rounded-md border border-border">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Direct director-level site accountability</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3.5">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  Request a Quote
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted">
                <Link to="/ProjectsCaseStudiesPage">
                  View Commercial Projects
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-border shadow-md bg-card">
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : <img src={imgSrc} data-wvc-srcset={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_overview_site_gantry.webp-wvc-srcset" : ""} data-wvc-sizes={isFallback ? "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_overview_site_gantry.webp-wvc-sizes" : ""} alt={imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
              </div>
              <div className="p-4 bg-card border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-medium text-foreground flex items-center gap-1.5">
                  <FileText className="size-3.5 text-primary" /> Multi-Storey Commercial Rig
                </span>
                <span>Pedestrian gantry & full debris containment</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Capability Specifications Grid */}
        <div className="mb-14">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-4 border-b border-border">
            <div>
              <span className="text-xs uppercase tracking-wider font-bold text-primary mb-1 block">
                Tailored Sector Delivery
              </span>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground font-default">
                Commercial client specifications
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md mt-2 md:mt-0 font-sans">
              Every sector demands distinct compliance, handover documentation, and site etiquette. Here is how we support your team.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commercialCapabilities.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.id} variants={itemVariants} data-index={i}>
                  <Card className="h-full bg-card border border-border rounded-lg shadow-sm hover:border-primary/60 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-3">
                      <div className="size-11 rounded-md bg-accent text-foreground flex items-center justify-center mb-3 border border-border">
                        <IconComp className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg font-bold text-foreground font-default">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <p className="text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                      <div className="pt-3 border-t border-border/80">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                          Key Standards
                        </span>
                        <p className="text-xs text-foreground font-medium">
                          {item.specs}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Accreditation & Direct Contact Strip */}
        <div className="bg-secondary text-secondary-foreground rounded-lg p-6 md:p-8 border border-border flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase font-bold tracking-widest text-primary">
                Verified Accreditations:
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {accreditationBadges.map((badge, i) => <div key={badge.name} data-index={i} className="bg-secondary-foreground/10 border border-secondary-foreground/20 px-3 py-1.5 rounded text-xs font-semibold text-secondary-foreground">
                  <span className="text-primary font-bold mr-1.5">{badge.name}</span>
                  <span className="opacity-85 text-[11px] hidden sm:inline">{badge.label}</span>
                </div>)}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-start lg:justify-end">
            <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Discuss a Project
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10">
              <Link to="/HealthSafetyAccreditationsPage">
                Safety Documentation
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
export default function Overview() {
  const wp_query = new WP_Query({
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <OverviewContent />
    </PostsProvider>;
}